import { type CookieConsentConfig } from "vanilla-cookieconsent";

import { m } from "@/paraglide/messages.js";
import { localizeHref } from "@/paraglide/runtime.js";

/**
 * Consent categories the preferences modal offers. `necessary` is read-only:
 * session and CSRF cookies cannot be refused. Extend this object (and the
 * matching modal section below) to add a category; vanilla-cookieconsent keys
 * consent state by these names, so renaming one resets stored consent.
 */
export const COOKIE_CATEGORIES = {
  necessary: "necessary",
  analytics: "analytics",
} as const;

export type CookieCategory = (typeof COOKIE_CATEGORIES)[keyof typeof COOKIE_CATEGORIES];

/**
 * Built at call time (not module scope) so every string reads the active
 * paraglide locale. Consent choices persist in the `cc_cookie` first-party
 * cookie; vanilla-cookieconsent also fires `cc:onConsent` / `cc:onChange` on
 * `window` for script gating.
 */
export const buildCookieConsentConfig = (): CookieConsentConfig => ({
  guiOptions: {
    consentModal: {
      layout: "box",
      position: "bottom right",
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: "box",
      equalWeightButtons: true,
      flipButtons: false,
    },
  },
  categories: {
    [COOKIE_CATEGORIES.necessary]: {
      enabled: true,
      readOnly: true,
    },
    [COOKIE_CATEGORIES.analytics]: {
      // Withdrawing consent erases already-set analytics cookies.
      autoClear: {
        cookies: [{ name: /^ph_/ }],
      },
    },
  },
  language: {
    default: "en",
    translations: {
      en: {
        consentModal: {
          title: m.cookies_consent_title(),
          description: `${m.cookies_consent_description()} <a href="${localizeHref("/cookies")}">${m.legal_cookies_title()}</a>`,
          acceptAllBtn: m.cookies_accept_all(),
          acceptNecessaryBtn: m.cookies_accept_necessary(),
          showPreferencesBtn: m.cookies_show_preferences(),
        },
        preferencesModal: {
          title: m.cookies_preferences_title(),
          acceptAllBtn: m.cookies_accept_all(),
          acceptNecessaryBtn: m.cookies_accept_necessary(),
          savePreferencesBtn: m.cookies_save_preferences(),
          closeIconLabel: m.cookies_close_label(),
          sections: [
            {
              title: m.cookies_usage_title(),
              description: m.cookies_usage_description(),
            },
            {
              title: m.cookies_necessary_title(),
              description: m.cookies_necessary_description(),
              linkedCategory: COOKIE_CATEGORIES.necessary,
            },
            {
              title: m.cookies_analytics_title(),
              description: m.cookies_analytics_description(),
              linkedCategory: COOKIE_CATEGORIES.analytics,
            },
          ],
        },
      },
    },
  },
});
